import Head from 'next/head';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import isEmpty from 'lodash/isEmpty';
import QuizLayout from '@cont/Quiz/Layout';
import BranchDeepLinksScript from '@Analytics/BranchDeepLinksScript';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import {
  fetchQuizMinimalData,
  fetchActiveSessionData
} from '@actions/quizTaking';
import {
  getQuizDetails,
  getLastFinishedSessionUID
} from '@selectors/quizTaking';
import { getMeUid } from '@selectors/auth';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { redirectToRoute } from '@utils/route';
import setupInitialProps from '@utils/setupInitialProps';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import useConstants from '@api/hooks/useConstants';
import { getIsGTPGoal, getIsTestRevampGoal } from '@api/selectors/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import useFollowGoal from '@api/hooks/useFollowGoal';
import { useEffect } from 'react';

const QuizMetaInfo = ({ goalUID }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { query } = router;
  const { quizUID } = query;

  const meUID = useSelectorWithShallowEqual(getMeUid);
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const lastFinishedSessionUID = useSelectorWithShallowEqual(
    getLastFinishedSessionUID
  );
  const me = useMyInfo(getMe);
  const isPlusUser = hasActiveSubscriptionToGoal(me, goalUID);
  const isGTPGoal = useConstants(getIsGTPGoal(goalUID));
  const isRevampedTest = useConstants(getIsTestRevampGoal(goalUID));

  const name = `${quizDetails?.title} | Unacademy`;
  const description = quizDetails?.description;
  const canonicalURL =
    quizDetails?.slug && quizDetails?.uid
      ? `https://unacademy.com/quiz/${quizDetails.slug}/${quizDetails.uid}/`
      : null;

  const openGraph = {
    title: name,
    description,
    url: canonicalURL,
    images: [
      {
        url:
          quizDetails?.ogImage ||
          quizDetails?.thumbnail ||
          quizDetails?.coverPhoto
      }
    ]
  };

  useFirstRenderSkip(() => {
    dispatch(fetchQuizMinimalData(quizUID));
  }, [meUID]);

  useFirstRenderSkip(() => {
    // Don't auto-resume the session we just finished on this page - the
    // minimal-data refetch dispatched right after finishing can still
    // report it as `user_active_session` for a moment (backend propagation
    // lag), which would otherwise look identical to a genuinely different
    // ongoing session needing a resume.
    if (
      quizDetails.user_active_session &&
      quizDetails.user_active_session !== lastFinishedSessionUID
    ) {
      dispatch(
        fetchActiveSessionData(
          quizUID,
          quizDetails.user_active_session,
          isRevampedTest
        )
      );
    }
  }, [quizDetails.user_active_session, isRevampedTest, lastFinishedSessionUID]);

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { followCurrentGoal } = useFollowGoal(goalUID);
  useEffect(() => {
    if (!goalUID) return;
    if (!isPlusUser && isGTPGoal) {
      router.replace(`/goal/-/${goalUID}`);
    }
  }, [goalUID, isGTPGoal, isPlusUser, router]);

  useEffect(() => {
    if (isLoggedIn) {
      followCurrentGoal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <>
      <NextSeo
        title={name}
        description={description}
        canonical={canonicalURL}
        openGraph={openGraph}
      />
      <Head>
        <title>{name}</title>
        {canonicalURL && <link rel="canonical" href={canonicalURL} />}
      </Head>
      <BranchDeepLinksScript />
    </>
  );
};

QuizMetaInfo.pageInfo = {
  name: 'Test Details Page',
  isMWebSupported: true,
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true,
    disableDarkMode: true
  }
};

QuizMetaInfo.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, res }) => {
    const { quizUID } = query;

    await store.dispatch(fetchQuizMinimalData(quizUID));

    const quizDetails = getQuizDetails(quizUID)(store.getState());
    const goalUID = quizDetails.goal_uid;

    if (isEmpty(quizDetails)) {
      return redirectToRoute(isServer, res, '/');
    }

    const { user_active_session } = getQuizDetails(quizUID)(store.getState());

    if (user_active_session) {
      await store.dispatch(
        fetchActiveSessionData(quizUID, user_active_session, false)
      );
    }
    return { goalUID };
  },

  QuizMetaInfo.pageInfo
);

QuizMetaInfo.Layout = QuizLayout;

export default QuizMetaInfo;
QuizMetaInfo.Wrapper = PageReduxProvider;
